<template>
  <section class="upload-details">
    <header>
      <div class="flow-btn">
        <button @click="previousStep">{{ $t('buttons.back') }}</button>
      </div>
      <div class="title">
        <h1>{{ $t('create.detailsTitle') }}:</h1>
        <p>{{ $t('create.detailsSubtitle') }}: JPG, PNG</p>
      </div>
      <div class="flow-btn">
        <button :class="{ 'alt-btn': !isDirty }" @click="nextStep">{{ $t(nextLabel) }}</button>
      </div>
    </header>
    <div class="content">
      <div class="left">
        <div class="content-group tags">
          <template v-for="tag in checkedTags">
            <div class="tag" :key="tag.id" v-if="tag.checked && tag.options">
              <label>
                {{ $t('tags.' + tag.id) }}
                <span v-if="tag.single">({{ $t('labels.selectOne') }})</span>:
              </label>
              <multiselect
                v-model="tag.value"
                :options="filterTagOptions(tag)"
                :label="tag.id === 'subscriptionPlan' ? 'subscriptionPlanName' : 'name'"
                :placeholder="$t('labels.selectOption')"
                :show-labels="false"
                :multiple="!tag.single"
                :close-on-select="!!tag.single"
                :loading="tag.loading"
                :track-by="tag.id === 'subscriptionPlan' ? 'unique_id' : 'id'"
              >
              </multiselect>
            </div>
          </template>
          <p>{{ $t('create.detailsForgotCategory') }}
            <router-link :to="`/media/${$route.params.media}/hmi`">{{ $t('create.detailsForgotAddMore')}}</router-link>.
          </p>
        </div>
        <div class="content-group metadata">
          <div class="cost">
            <label for="cost">{{ $t('labels.cost') }} ({{ $t('labels.optional') }})</label>
            <masked-input
              id="cost"
              type="tel"
              v-model="cost"
              :mask="numberMask"
              placeholder="$0.00"
              :guide="false"
            />
          </div>
          <div class="comments">
            <label for="comments">{{ $t('media.comments') }}</label>
            <textarea
              id="comments"
              v-model="comments"
              :placeholder="$t('create.detailsCommentsPlaceholder')"
            ></textarea>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="screens" ref="screens">
          <template>
            <div class="screen-title" v-if="$store.state.media.country === 'CAN'">
              <div class="line" />
              <div class="title">Images (English)</div>
              <div class="line" />
            </div>
            <upload-screen
              v-if="!isSubscriptionPlan"
              v-model="isChecked1En"
              :files="screen1FilesEn"
              :list="screen1ListEn"
              :label="$t('media.screen') + ' 1 (' + $t('media.screensaver') + ')'"
              language="en"
            >
              <div class="duration">
                <label for="duration">{{ $t('media.duration') + ' (' + $t('labels.seconds') + ')' }}</label>
                <input id="duration" type="number" v-model="duration" @blur="calculateDuration">
              </div>
            </upload-screen>
            <upload-screen
              v-if="!isSubscriptionPlan"
              v-model="isChecked2En"
              :files="screen2FilesEn"
              :list="screen2ListEn"
              :label="$t('media.screen') + ' 2 (' + $t('media.presentPayment') + ')'"
              language="en"
            />
            <upload-screen
              v-model="isChecked3En"
              :files="screen3FilesEn"
              :list="screen3ListEn"
              :label="$t('media.screen') + ' 3 (' + $t('media.payment') + ')'"
              language="en"
            />
            <upload-screen
              v-model="isChecked4En"
              :files="screen4FilesEn"
              :list="screen4ListEn"
              :label="$t('media.screen') + ' 4 (' + $t('media.thankYou') + ')'"
              language="en"
            />
          </template>
          <template v-if="$store.state.media.country === 'CAN'">
            <div class="screen-title" style="margin-top: 50px;">
              <div class="line" />
              <div class="title">Images (French)</div>
              <div class="line" />
            </div>
            <upload-screen
              v-if="!isSubscriptionPlan"
              v-model="isChecked1Fr"
              :files="screen1FilesFr"
              :list="screen1ListFr"
              :label="$t('media.screen') + ' 1 (' + $t('media.screensaver') + ')'"
              language="fr"
            >
              <div class="duration">
                <label for="duration">{{ $t('media.duration') + ' (' + $t('labels.seconds') + ')' }}</label>
                <input id="duration" type="number" v-model="duration" @blur="calculateDuration">
              </div>
            </upload-screen>
            <upload-screen
              v-if="!isSubscriptionPlan"
              v-model="isChecked2Fr"
              :files="screen2FilesFr"
              :list="screen2ListFr"
              :label="$t('media.screen') + ' 2 (' + $t('media.presentPayment') + ')'"
              language="fr"
            />
            <upload-screen
              v-model="isChecked3Fr"
              :files="screen3FilesFr"
              :list="screen3ListFr"
              :label="$t('media.screen') + ' 3 (' + $t('media.payment') + ')'"
              language="fr"
            />
            <upload-screen
              v-model="isChecked4Fr"
              :files="screen4FilesFr"
              :list="screen4ListFr"
              :label="$t('media.screen') + ' 4 (' + $t('media.thankYou') + ')'"
              language="fr"
            />
          </template>
        </div>
      </div>
    </div>
    <footer>
      <button @click="previousStep">{{ $t('buttons.back') }}</button>
      <button :class="{ 'alt-btn': !isDirty }" @click="nextStep">{{ $t(nextLabel) }}</button>
    </footer>
  </section>
</template>

<script>
import UploadScreen from '@/components/UploadScreen.vue'
import Multiselect from 'vue-multiselect'
import MaskedInput from 'vue-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { GET_CRITERIA, SAVE_MEDIA } from '@/store/actions.type'
import {
  SET_UPLOAD_TAGS,
  SET_SCREEN_STATE,
  SET_DETAILS
} from '@/store/mutations.type'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'upload-details',
  components: {
    UploadScreen,
    Multiselect,
    MaskedInput
  },
  data() {
    return {
      duration: null,
      cost: null,
      comments: null,
      numberMask: createNumberMask({
        allowDecimal: true
      }),
      isChecked1En: false,
      isChecked2En: false,
      isChecked3En: false,
      isChecked4En: false,
      isChecked1Fr: false,
      isChecked2Fr: false,
      isChecked3Fr: false,
      isChecked4Fr: false,
      checkedTags: this.$store.getters.tags.slice()
    }
  },
  computed: {
    ...mapState({
      screen1ListEn: state => state.media.screen1ListEn,
      screen2ListEn: state => state.media.screen2ListEn,
      screen3ListEn: state => state.media.screen3ListEn,
      screen4ListEn: state => state.media.screen4ListEn,
      screen1ListFr: state => state.media.screen1ListFr,
      screen2ListFr: state => state.media.screen2ListFr,
      screen3ListFr: state => state.media.screen3ListFr,
      screen4ListFr: state => state.media.screen4ListFr,
    }),
    ...mapGetters({
      screen1FilesEn: 'screen1En',
      screen2FilesEn: 'screen2En',
      screen3FilesEn: 'screen3En',
      screen4FilesEn: 'screen4En',
      screen1FilesFr: 'screen1Fr',
      screen2FilesFr: 'screen2Fr',
      screen3FilesFr: 'screen3Fr',
      screen4FilesFr: 'screen4Fr',
      partners: 'partners'
    }),
    isDirty() {
      return (
        this.checkedTags.some(tag => tag.checked && tag.value) ||
        this.cost ||
        this.comments ||
        this.isChecked1En ||
        this.isChecked2En ||
        this.isChecked3En ||
        this.isChecked4En ||
        this.isChecked1Fr ||
        this.isChecked2Fr ||
        this.isChecked3Fr ||
        this.isChecked4Fr
      )
    },
    nextLabel() {
      return this.isDirty ? 'buttons.finalReview' : 'buttons.skipForNow'
    },
    isSubscriptionPlan() {
      const subscriptionTag = this.checkedTags.find(tag => tag.id === 'subscriptionPlan');
      return subscriptionTag && subscriptionTag.checked;
    }
  },
  watch: {
    '$store.getters.tags': function(tags) {
      this.setupTagOptions(tags)
    }
  },
  methods: {
    previousStep() {
      this.$emit('back')
    },
    nextStep() {
      this.$store.commit(SET_UPLOAD_TAGS, this.checkedTags)
      this.$store.commit(SET_SCREEN_STATE, [
        this.isChecked1En,
        this.isChecked2En,
        this.isChecked3En,
        this.isChecked4En,
        this.isChecked1Fr,
        this.isChecked2Fr,
        this.isChecked3Fr,
        this.isChecked4Fr
      ])
      this.$store.commit(SET_DETAILS, {
        duration: this.duration,
        cost: this.cost ? this.cost.substring(1) : null,
        comments: this.comments
      })
      this.$store.dispatch(SAVE_MEDIA, false)
      this.$emit('next')
    },
    calculateDuration() {
      if (this.duration === '') return

      const duration = parseInt(this.duration, 10)
      if (duration) this.duration = duration
      else this.duration = ''
    },
    filterTagOptions(tag) {
      return tag.options.filter(item => item.country === this.$store.state.media.country || item.source === this.$store.state.media.country);
    },
    setupTagOptions(tags) {
      tags.forEach((tag, index) => {
        this.$set(this.checkedTags[index], 'options', tag.options)
        if (!this.checkedTags[index].value && tag.value) {
          this.$set(
            this.checkedTags[index],
            'value',
            Array.isArray(tag.value) ? tag.value.slice() : Object.assign({}, tag.value)
          )
        }
      })
    }
  },
  created() {
    this.checkedTags.forEach(tag => {
      this.$set(tag, 'loading', true)
    })
    this.$store.dispatch(GET_CRITERIA).then(() => {
      this.setupTagOptions(this.checkedTags)
      this.checkedTags.forEach(tag => {
        this.$set(tag, 'loading', false)
      })
    })
    this.isChecked1En = this.screen1FilesEn.some(file => !!file.image)
    this.isChecked2En = this.screen2FilesEn.some(file => !!file.image)
    this.isChecked3En = this.screen3FilesEn.some(file => !!file.image)
    this.isChecked4En = this.screen4FilesEn.some(file => !!file.image)
    this.isChecked1Fr = this.screen1FilesFr.some(file => !!file.image)
    this.isChecked2Fr = this.screen2FilesFr.some(file => !!file.image)
    this.isChecked3Fr = this.screen3FilesFr.some(file => !!file.image)
    this.isChecked4Fr = this.screen4FilesFr.some(file => !!file.image)

    this.duration = this.$store.state.media.duration
    this.cost = this.$store.state.media.cost ? ('$' + this.$store.state.media.cost) : null
    this.comments = this.$store.state.media.comments
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.content {
  display: flex;

  & > .left {
    flex: 1 0 auto;
    max-width: 500px;
  }
  & > .right {
    flex: 1 0 875px;
    margin-left: 25px;
    // max-width: 875px;
  }
}
.content-group {
  background: #fff;
  margin-bottom: 25px;
  padding: 50px;
}
.content {
  label {
    color: #898989;
    text-align: left;
    display: block;
  }
  .tag + .tag {
    margin-top: 30px;
  }
  .tags p {
    text-align: left;
    margin-top: 40px;
  }
  /deep/ {
    .duration {
      margin-top: 25px;
      width: 150px;
    }
    .multiselect__select {
      top: auto;
      bottom: 1px;
    }
    .multiselect__tags {
      border-color: #0b0f4d;
      border-width: 0 0 1px;
      padding-left: 0;

      .multiselect__single,
      .multiselect__tag {
        color: #4399b0;
        background-color: #cbeef3;
        border-radius: 4px;
        display: inline-block;
        margin-bottom: em(6);
        padding: em(9) em(20);
        width: auto;
      }
      .multiselect__tag-icon {
        line-height: 1em;
        display: inline-block;
        margin-left: 0;
        position: static;
        width: 1em;
        vertical-align: 0.05em;

        &:focus,
        &:hover {
          color: #4399b0;
          background-color: #cbeef3;
        }
        &::after {
          color: inherit;
        }
      }
    }
    .multiselect__input {
      display: block;
      padding-left: 0;
    }
  }
}
.metadata {
  text-align: left;

  .cost {
    display: inline-block;
    width: 125px;
  }
  .comments {
    margin-top: 50px;
    width: 100%;

    textarea {
      height: 1em;
    }
  }
}
.screens {
  min-height: 150px;
  position: relative;

  .screen-title {
    display: flex;
    align-items: center;
    max-width: 975px;
    margin: 0 auto 40px auto;

    .title {
      font: 600 16px/21px Omnes;
      letter-spacing: 0.4px;
      color: #0B0F4D;
      opacity: 1;
      margin: 0 25px;
    }

    .line {
      flex-grow: 1;
      border: 1px solid #ADADAD;
    }
  }
}
.screen {
  margin: 0 auto 25px;
  padding: 50px;
  max-width: 875px;
}
footer {
  display: flex;
  justify-content: space-between;
}
</style>
